import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUser, updateUser, deleteUser } from '@/api/users';
import { Card, Typography, Button, Space, List, Tabs, Modal, message, Row, Col, Empty, Grid, Divider } from 'antd';
import { EditOutlined, DeleteOutlined, LockOutlined, PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import type { Contact } from '../../types/common';
import UserModal from '../../components/features/users/UserModal';
import ContactEditModal from '../../components/modal/ContactEditModal';
import ChangePasswordModal from '../../components/features/users/ChangePasswordModal';
import ConfirmDeleteModal from '../../components/modal/ConfirmDeleteModal';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import type { UpdateUserInput } from '@/types/users';
import { UserInfoTab } from '@/components/tab';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const UserDetailsPage: React.FC = () => {
  const { t } = useTranslation(['users', 'common']);
  const { id } = useParams<{ id: string }>();

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Contacts modal state
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | undefined>(undefined);

  const { firestoreUser } = useAuth();

  const { data: user, isLoading } = useQuery({
    queryKey: ['user', id],
    queryFn: () => {
      if (!id || !firestoreUser) return null;
      return getUser(id, firestoreUser);
    },
    enabled: !!id && !!firestoreUser
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateUserInput) => {
      if (!firestoreUser || !id) throw new Error('User not authenticated');
      return updateUser(id, data, firestoreUser);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success(t('users:messages.updateSuccess'));
    },
    onError: (error: Error) => {
      message.error(error.message || t('users:messages.updateFailed'));
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteUser(userId, firestoreUser);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      message.success(t('users:messages.deleteSuccess'));
      navigate('/users');
    },
    onError: (error: Error) => {
      message.error(error.message || t('users:messages.deleteFailed'));
    }
  });

  const handleEdit = () => {
    setIsModalOpen(true);
  };

  const handlePasswordChange = () => {
    setIsPasswordModalOpen(true);
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteMutation.mutateAsync(id);
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  // Contacts handlers
  const handleAddContact = () => {
    setEditingContact(undefined);
    setContactModalOpen(true);
  };

  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setContactModalOpen(true);
  };

  const handleDeleteContact = (contact: Contact) => {
    Modal.confirm({
      title: t('users:deleteContact'),
      content: t('users:deleteContactConfirmation'),
      okText: t('common:actions.delete'),
      okType: 'danger',
      cancelText: t('common:actions.cancel'),
      onOk: () => {
        const newContacts = (user?.contacts || []).filter(c => c !== contact);
        updateMutation.mutate({ contacts: newContacts });
      },
    });
  };

  const handleSaveContact = (contacts: Contact[]) => {
    let newContacts = user?.contacts ? [...user.contacts] : [];
    if (editingContact) {
      // Edit
      newContacts = newContacts.map(c => (c === editingContact ? contacts[0] : c));
    } else {
      // Add
      newContacts.push(contacts[0]);
    }
    updateMutation.mutate({ contacts: newContacts });
    setContactModalOpen(false);
    setEditingContact(undefined);
  };

  if (isLoading || !user) return <Card loading />;

  return (
    <Card>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col>
            <Space>
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/users')}
              >
                {t('common:back')}
              </Button>
              <Title level={3} style={{ margin: 0 }}>
                {user.displayName || user.email}
              </Title>
            </Space>
          </Col>
          <Col>
            <Space direction={isMobile ? "vertical" : "horizontal"} size="small">
              <Button 
                icon={<LockOutlined />} 
                onClick={handlePasswordChange}
                block={isMobile}
              >
                {t('users:changePassword')}
              </Button>
              <Button 
                icon={<EditOutlined />} 
                onClick={handleEdit}
                block={isMobile}
              >
                {t('common:actions.edit')}
              </Button>
              <Button 
                danger 
                icon={<DeleteOutlined />} 
                onClick={handleDelete}
                block={isMobile}
              >
                {t('common:actions.delete')}
              </Button>
            </Space>
          </Col>
        </Row>

        <Divider />

        <Tabs
          defaultActiveKey="details"
          items={[
            {
              key: 'details',
              label: t('users:details'),
              children: (
                <UserInfoTab userId={user.id} />
              ),
            },
            {
              key: 'contacts',
              label: t('users:contacts'),
              children: (
                <Card
                  title={
                    <Row justify="space-between" align="middle">
                      <Col>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddContact}>
                          {t('users:addContact')}
                        </Button>
                      </Col>
                    </Row>
                  }
                >
                  {(user.contacts && user.contacts.length > 0) ? (
                    <List
                      dataSource={user.contacts}
                      renderItem={(contact: Contact) => (
                        <List.Item
                          actions={[
                            <Button type="link" onClick={() => handleEditContact(contact)}>
                              {t('common:actions.edit')}
                            </Button>,
                            <Button type="link" danger onClick={() => handleDeleteContact(contact)}>
                              {t('common:actions.delete')}
                            </Button>,
                          ]}
                        >
                          <List.Item.Meta
                            title={contact.name}
                            description={
                              <Space direction="vertical" size={0}>
                                <Text type="secondary">{t('common:email')}: {contact.email}</Text>
                                <Text type="secondary">{t('common:phone')}: {contact.phone}</Text>
                              </Space>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  ) : (
                    <Empty description={t('users:noContacts')} />
                  )}
                </Card>
              ),
            },
          ]}
        />
      </Space>

      <UserModal
        visible={isModalOpen}
        user={user}
        onCancel={() => setIsModalOpen(false)}
        onSuccess={() => setIsModalOpen(false)}
      />

      <ContactEditModal
        open={contactModalOpen}
        contact={editingContact}
        onCancel={() => {
          setContactModalOpen(false);
          setEditingContact(undefined);
        }}
        onSave={handleSaveContact}
        contactTypeOptions={[
          { value: 'Primary', label: t('common:contactTypes.primary') },
          { value: 'Secondary', label: t('common:contactTypes.secondary') },
          { value: 'Decision Maker', label: t('common:contactTypes.decisionMaker') },
        ]}
        t={t}
      />

      <ChangePasswordModal
        visible={isPasswordModalOpen}
        user={user}
        onCancel={() => setIsPasswordModalOpen(false)}
        onSuccess={() => setIsPasswordModalOpen(false)}
      />

      <ConfirmDeleteModal
        open={isDeleteModalOpen}
        title={t('users:deleteUser')}
        description={t('users:deleteUserConfirmation', { name: user.displayName || user.email })}
        onOk={() => {
          handleDelete();
          setIsDeleteModalOpen(false);
        }}
        onCancel={() => setIsDeleteModalOpen(false)}
      />
    </Card>
  );
};

export default UserDetailsPage; 